import { useEffect, useState } from "react";

/**
 * Page state for a *server*-paginated sourcing list.
 *
 * The sourcing list endpoints (/sourcing/saved, /sourcing/shortlist,
 * /sourcing/contacted, /engagement/responses) take `page` + `limit` and return that
 * page plus `total` — the count of every matching row — exactly like the interview
 * endpoints. This hook owns only the page/size state and the reset rules; the
 * component does the fetching and feeds `totalItems` back in so an out-of-range page
 * can be corrected.
 *
 * For a list the API still returns whole (search results), use usePagination instead.
 *
 * @param {Object} options
 * @param {number} options.totalItems      total matching rows, from the server response
 * @param {number} options.initialPageSize rows per page on first render
 * @param {*}      options.resetKey        changes to this send the user back to page 1
 *                                         (pass a string built from the active filters)
 */
export default function useServerPagination({
    totalItems = 0,
    initialPageSize = 10,
    resetKey = null,
} = {}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(initialPageSize);

    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

    // Filters/search/sort changed — the old page number no longer means anything.
    useEffect(() => {
        setCurrentPage(1);
    }, [resetKey]);

    // The result set can shrink under us (unsave, un-shortlist, refetch). Without this
    // the user is stranded on a page the server now has no rows for.
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (size) => {
        setItemsPerPage(size);
        setCurrentPage(1);
    };

    return {
        currentPage,
        itemsPerPage,
        totalPages,
        handlePageChange,
        handleItemsPerPageChange,
        setCurrentPage,
    };
}
